import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Nav from "./Nav";
import Footer from "./Footer";
import { safeFetch } from "@/lib/sanity";
import { siteSettingsQuery } from "@/lib/queries";
import { siteSettingsFallback } from "@/lib/fallback";
import type { Seo, SiteSettings } from "@/lib/types";

/** Scrolls to the top whenever the pathname changes — basic SPA manners. */
function useScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Let hash navigation do its thing via smooth scroll in CSS.
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);
}

interface LayoutProps {
  seo: Seo;
  children: React.ReactNode;
}

export default function Layout({ seo, children }: LayoutProps) {
  useScrollToTop();
  const [settings, setSettings] = useState<SiteSettings>(siteSettingsFallback);

  useEffect(() => {
    let active = true;
    safeFetch<SiteSettings>(siteSettingsQuery).then((data) => {
      if (active && data) setSettings(data);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        {seo.title && <title>{seo.title}</title>}
        {seo.description && <meta name="description" content={seo.description} />}
        {seo.title && <meta property="og:title" content={seo.title} />}
        {seo.description && <meta property="og:description" content={seo.description} />}
        {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <Nav settings={settings} />
      <main id="main">{children}</main>
      <Footer settings={settings} />
    </>
  );
}
